import {
  AgentMedia,
  AGMediaKeys,
  Association,
  AssociationKeys,
  CampaignLogoHeadshot,
  CampaignSocialMedia,
  ChangeSourceType,
  EmailAddress,
  EmailAddressKeys,
  MediaContentType,
  PhoneNumber,
  PhoneNumberKeys,
  Social,
  SocialKeys,
} from '@ag-common-lib/public-api';

import { firstValueFrom, map } from 'rxjs';
import { LookupsService } from '../services';
import { AgentEmailAddressesService } from '../services/agent-email-addresses.service';
import { AgentImagesService } from '../services/agent-images.service';
import { getPhoneNumberKeyExpression } from '../components/select-phone-number/select-phone-number.service';
import { omit } from 'lodash';
import { AgentAssociationsService } from '../services/agent-associations.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  BatchCommand,
  BatchCommandKeys,
  BatchCommandType,
  BatchOperationsService,
} from '../dao/batch-firestore.service';

export class AgentProfileDataService {
  constructor(
    protected readonly lookupsService: LookupsService,
    protected readonly agentEmailAddressesService: AgentEmailAddressesService,
    protected readonly agentAssociationsService: AgentAssociationsService,
    protected readonly agentImagesUtil: AgentImagesService,
  ) {}

  protected handleAgentPhoneNumber = (
    phoneNumber: Partial<PhoneNumber>,
    agentPhoneNumbers: PhoneNumber[],
  ): PhoneNumber[] => {
    if (!agentPhoneNumbers?.length) {
      return [
        Object.assign({}, new PhoneNumber(), phoneNumber, {
          [PhoneNumberKeys.is_primary]: true,
        }),
      ];
    }

    const existedPhoneNumber = agentPhoneNumbers.find(agentPhoneNumber => {
      const agentPhoneNumberKeyExpression = getPhoneNumberKeyExpression(agentPhoneNumber);

      return getPhoneNumberKeyExpression(phoneNumber) === agentPhoneNumberKeyExpression;
    });

    existedPhoneNumber
      ? Object.assign(existedPhoneNumber, omit(phoneNumber, [PhoneNumberKeys.is_primary]))
      : agentPhoneNumbers.push(Object.assign({}, new PhoneNumber(), omit(phoneNumber, [PhoneNumberKeys.is_primary])));

    return agentPhoneNumbers;
  };

  protected handleAgentSocials = async (
    campaignSocialMedia: Partial<CampaignSocialMedia>,
    agentSocials: Social[],
  ): Promise<Social[]> => {
    const defaultSocialTypesLookup = await firstValueFrom(
      this.lookupsService.socialTypesLookup$.pipe(
        map(socialTypes => socialTypes?.find(socialType => socialType?.isDefault)),
      ),
    );

    const newSocials = [];

    Object.entries(campaignSocialMedia)
      .filter(([_key, value]) => !!value)
      .forEach(([media, url]) => {
        const isMediaExist = agentSocials?.length && agentSocials.some(media => media.url === url);

        if (!isMediaExist) {
          newSocials.push(
            Object.assign({}, new Social(), {
              [SocialKeys.url]: url,
              [SocialKeys.socialType]: defaultSocialTypesLookup,
              [SocialKeys.socialMedia]: media,
            }),
          );
        }
      });

    return [...agentSocials, ...newSocials];
  };

  protected getAgentEmailAddressCommand = async (
    emailAddress: Partial<EmailAddress>,
    agentDbId: string,
    actionName: ChangeSourceType | null,
  ): Promise<BatchCommand<EmailAddress>> => {
    if (emailAddress?.[EmailAddressKeys.isLogin]) {
      return null;
    }

    const table = AgentEmailAddressesService.getCollectionPath(agentDbId);
    const command: Omit<BatchCommand, BatchCommandKeys.type | BatchCommandKeys.dbId> = {
      [BatchCommandKeys.table]: table,
      [BatchCommandKeys.toFirestore]: AgentEmailAddressesService.toFirestore,
    };

    if (emailAddress?.[BaseModelKeys.dbId]) {
      return Object.assign({}, command, {
        [BatchCommandKeys.type]: BatchCommandType.update,
        [BatchCommandKeys.dbId]: emailAddress?.[BaseModelKeys.dbId],
        [BatchCommandKeys.data]: {
          [BaseModelKeys.actionName]: actionName,
          [EmailAddressKeys.address]: emailAddress?.[EmailAddressKeys.address],
        },
      });
    }
    const defaultEmailTypeLookup = await firstValueFrom(
      this.lookupsService.emailTypeLookup$.pipe(map(emailTypes => emailTypes?.find(emailType => emailType?.isDefault))),
    );

    return Object.assign({}, command, {
      [BatchCommandKeys.type]: BatchCommandType.set,
      [BatchCommandKeys.data]: {
        [BaseModelKeys.actionName]: actionName,
        [EmailAddressKeys.agentDbId]: agentDbId,
        [EmailAddressKeys.emailType]: defaultEmailTypeLookup?.dbId,
        [EmailAddressKeys.address]: emailAddress?.[EmailAddressKeys.address],
      },
    });
  };

  protected handleAgentEmailAddress = async (
    emailAddress: Partial<EmailAddress>,
    agentDbId: string,
    actionName: ChangeSourceType | null,
  ) => {
    if (emailAddress?.[EmailAddressKeys.isLogin]) {
      return;
    }

    if (emailAddress?.[BaseModelKeys.dbId]) {
      return this.agentEmailAddressesService.update(
        agentDbId,
        emailAddress?.[BaseModelKeys.dbId],
        {
          [EmailAddressKeys.address]: emailAddress?.[EmailAddressKeys.address],
        },
        true,
        actionName,
      );
    }
    const defaultEmailTypeLookup = await firstValueFrom(
      this.lookupsService.emailTypeLookup$.pipe(map(emailTypes => emailTypes?.find(emailType => emailType?.isDefault))),
    );

    return this.agentEmailAddressesService.create(
      agentDbId,
      {
        [EmailAddressKeys.agentDbId]: agentDbId,
        [EmailAddressKeys.emailType]: defaultEmailTypeLookup?.dbId,
        [EmailAddressKeys.address]: emailAddress?.[EmailAddressKeys.address],
      },
      true,
      actionName,
    );
  };

  protected handleAgentLogoHeadshot = (
    campaignLogoHeadshot: Partial<CampaignLogoHeadshot>,
    agentDbId: string,
  ): Promise<AgentMedia | void>[] => {
    return Object.values(campaignLogoHeadshot)
      .filter(item => item && !item?.[BaseModelKeys.dbId])
      .map(item => {
        const media = Object.assign({}, new AgentMedia(/* MediaContentType.image, */ agentDbId), {
          [AGMediaKeys.contentType]: item?.contentType ?? null,
          [AGMediaKeys.wasabiPath]: item?.wasabiPath ?? null,
          [AGMediaKeys.fileName]: item?.fileName ?? null,
        });
        return this.agentImagesUtil.addAgentMediaToImagesDirectory(agentDbId, media);
      });
  };
}
